import { formatMoney, MAX_DISCOUNT_PERCENTAGE } from "@/lib/financialCalculations";
import { cn } from "@/lib/utils";

interface PriceWithAdjustmentProps {
  /** Raw, pre-adjustment amount (catalog price × pax/quantity, or user-entered value). */
  baseAmount: number;
  /** Proposal data — only `pricing_adjustment_type`, `pricing_adjustment_percentage`, `currency` are used. */
  proposalData?: {
    pricing_adjustment_type?: string | null;
    pricing_adjustment_percentage?: number | string | null;
    currency?: string | null;
  } | null;
  /** Set to false for categories that don't receive the adjustment (e.g. handling fee). Defaults to true. */
  adjustable?: boolean;
  /** Optional label shown when amount is 0 (e.g. "No service", "To Be Quoted"). */
  emptyLabel?: string;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /** Visual size — "sm" matches existing summary cards, "md" gives a slightly larger figure. */
  size?: "sm" | "md";
}

/**
 * Renders a per-item price. When the proposal has a commission/discount applied
 * AND the category is adjustable, shows the base (strike-through) next to the
 * adjusted value. Used by item cards across all proposal steps for consistent
 * "you'll see base + final once a markup is applied" behavior.
 */
export const PriceWithAdjustment = ({
  baseAmount,
  proposalData,
  adjustable = true,
  emptyLabel,
  className,
  size = "sm",
}: PriceWithAdjustmentProps) => {
  const type = (proposalData?.pricing_adjustment_type || "none").toString();
  const percentageRaw = Number(proposalData?.pricing_adjustment_percentage || 0);
  const percentageValue = Number.isFinite(percentageRaw) ? percentageRaw : 0;
  // A discount is capped at 100%; clamped here too so a legacy row above it renders a free
  // item rather than a negative price.
  const percentage =
    type === "discount" ? Math.min(percentageValue, MAX_DISCOUNT_PERCENTAGE) : percentageValue;
  const currency =
    String(proposalData?.currency || "").trim().toUpperCase() === "AED" ? "AED" : "USD";

  const format = (value: number) => formatMoney(value, currency, "en-US");

  const hasAppliedAdjustment = type !== "none" && percentage > 0;
  const factor =
    type === "commission" ? 1 + percentage / 100 :
    type === "discount"   ? 1 - percentage / 100 :
    1;

  const finalClass = size === "md" ? "text-base font-semibold text-primary tabular-nums" : "text-sm font-semibold text-primary tabular-nums";
  const struckClass = size === "md" ? "text-sm text-muted-foreground line-through tabular-nums" : "text-xs text-muted-foreground line-through tabular-nums";

  // Empty-label fallback (e.g. "No service") — only when there's no real value AND a label is provided.
  if (baseAmount === 0 && emptyLabel) {
    return <span className={cn(finalClass, className)}>{emptyLabel}</span>;
  }

  // No adjustment to show OR category not adjustable OR amount is 0 → single value.
  if (baseAmount === 0 || !hasAppliedAdjustment || !adjustable || factor === 0) {
    return <span className={cn(finalClass, className)}>{format(baseAmount)}</span>;
  }

  const adjusted = baseAmount * factor;
  return (
    <span className={cn("inline-flex flex-wrap items-baseline gap-1.5", className)}>
      <span className={struckClass}>{format(baseAmount)}</span>
      <span className={finalClass}>{format(adjusted)}</span>
    </span>
  );
};

export default PriceWithAdjustment;
